import React from 'react';
import {MessageSquare} from 'lucide-react';

/**
 * Progress Wheel Viewer Component
 * Displays the progress wheel from teacher feedback (read-only)
 */
const ProgressWheelViewer = ({progressWheel}) => {
    // Check if we have progress wheel data
    if (!progressWheel) {
        return null;
    }

    // Configuration
    const radius = 120;
    const center = 150;
    const abilities = ['Awareness', 'Sensitivity', 'Creativity'];
    const levels = ['Beginner', 'Proficient', 'Advanced'];
    const sources = ['Teacher', 'Student', 'Peer'];

    // Colors that match the image
    const sourceColors = {
        'Teacher': '#F5A15D', // Orange
        'Student': '#5DC695', // Green
        'Peer': '#9C8EDF'     // Purple
    };

    // Angle calculations
    const abilityAngle = 2 * Math.PI / abilities.length;
    const sourceAngle = abilityAngle / sources.length;

    // Helper functions
    const getCoords = (angle, r) => ({
        x: center + r * Math.cos(angle),
        y: center + r * Math.sin(angle)
    });

    // Get level for each ability and source
    const getLevels = () => {
        const result = {
            Teacher: {
                Awareness: progressWheel.awareness?.teacher || null,
                Sensitivity: progressWheel.sensitivity?.teacher || null,
                Creativity: progressWheel.creativity?.teacher || null
            },
            Student: {
                Awareness: progressWheel.awareness?.student || null,
                Sensitivity: progressWheel.sensitivity?.student || null,
                Creativity: progressWheel.creativity?.student || null
            },
            Peer: {
                Awareness: progressWheel.awareness?.peer || null,
                Sensitivity: progressWheel.sensitivity?.peer || null,
                Creativity: progressWheel.creativity?.peer || null
            }
        };
        return result;
    };

    // Get the levels from the progress wheel
    const allLevels = getLevels();

    // Render the segments
    const renderSegments = () => {
        const segments = [];
        const innerRadius = 40; // Center circle radius
        const levelWidth = 25;   // Width of each level ring

        // For each ability (Awareness, Sensitivity, Creativity)
        abilities.forEach((ability, abilityIndex) => {
            const abilityStartAngle = abilityIndex * abilityAngle - Math.PI / 2;
            const abilityLabelAngle = abilityStartAngle + abilityAngle / 2;

            // Add ability label on the outside
            const labelPos = getCoords(abilityLabelAngle, radius + 20);
            const abilitySide = abilityIndex % 3;
            const abilityColor = abilitySide === 0 ? '#F47B89' : abilitySide === 1 ? '#2EAAD9' : '#F5A15D';

            segments.push(
                <text
                    key={`${ability}-label`}
                    x={labelPos.x}
                    y={labelPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={14}
                    fontWeight="bold"
                    fill={abilityColor}
                    style={{textTransform: 'uppercase'}}
                    transform={`rotate(${(abilityLabelAngle * 180 / Math.PI) + 90}, ${labelPos.x}, ${labelPos.y})`}
                >
                    {ability}
                </text>
            );

            // For each source (Teacher, Student, Peer)
            sources.forEach((source, sourceIndex) => {
                const sourceStartAngle = abilityStartAngle + sourceIndex * sourceAngle;
                const sourceEndAngle = sourceStartAngle + sourceAngle;

                // For each level (Beginner, Proficient, Advanced)
                levels.forEach((level, levelIndex) => {
                    const levelStartRadius = innerRadius + levelIndex * levelWidth;
                    const levelEndRadius = levelStartRadius + levelWidth;

                    // Determine if this segment should be highlighted
                    const selectedLevel = allLevels[source][ability];
                    const isSelected = level === selectedLevel;

                    // Create arc path
                    const innerStart = getCoords(sourceStartAngle, levelStartRadius);
                    const innerEnd = getCoords(sourceEndAngle, levelStartRadius);
                    const outerStart = getCoords(sourceStartAngle, levelEndRadius);
                    const outerEnd = getCoords(sourceEndAngle, levelEndRadius);

                    const largeArcFlag = sourceEndAngle - sourceStartAngle > Math.PI ? 1 : 0;

                    const path = `
            M ${outerStart.x} ${outerStart.y}
            A ${levelEndRadius} ${levelEndRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
            L ${innerEnd.x} ${innerEnd.y}
            A ${levelStartRadius} ${levelStartRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}
            Z
          `;

                    segments.push(
                        <path
                            key={`${ability}-${source}-${level}`}
                            d={path}
                            fill={isSelected ? sourceColors[source] : '#f0f0f0'}
                            stroke="#fff"
                            strokeWidth={2}
                            opacity={isSelected ? 1 : 0.7}
                        />
                    );

                    // Add level label
                    if (isSelected) {
                        const labelRadius = levelStartRadius + levelWidth / 2;
                        const labelPos = getCoords(sourceStartAngle + sourceAngle / 2, labelRadius);

                        segments.push(
                            <text
                                key={`${ability}-${source}-${level}-label`}
                                x={labelPos.x}
                                y={labelPos.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={12}
                                fontWeight="bold"
                                fill="#fff"
                            >
                                {level.charAt(0)}
                            </text>
                        );
                    }
                });

                // Add small source label in the wheel
                const labelRadius = innerRadius / 2;
                const labelPos = getCoords(sourceStartAngle + sourceAngle / 2, innerRadius + levelWidth * 1.5);

                segments.push(
                    <text
                        key={`${ability}-${source}-label`}
                        x={labelPos.x}
                        y={labelPos.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={8}
                        fill="#666"
                        style={{textTransform: 'uppercase'}}
                    >
                        {source}
                    </text>
                );
            });
        });

        return segments;
    };

    return (
        <div>
            {/* Main Progress Wheel */}
            <svg width="300" height="300" viewBox="0 0 300 300" className="mx-auto">
                {/* Center circle */}
                <circle cx={center} cy={center} r={40} fill="white" stroke="#eee" strokeWidth={1}/>
                <text x={center} y={center - 8} textAnchor="middle" fontSize={10} fontWeight="bold">PERFORMANCE</text>
                <text x={center} y={center + 8} textAnchor="middle" fontSize={10} fontWeight="bold">LEVEL</text>

                {/* Performance icon */}
                <circle cx={center} cy={center} r={15} fill="#f9f9f9"/>
                <path
                    d="M145,150 L145,145 L150,142 L155,146 L155,150 Z M142,155 L142,152 L147,150 L152,153 L152,155 Z M158,155 L158,153 L153,150 L148,152 L148,155 Z"
                    fill="#555"
                />

                {/* Wheel segments */}
                {renderSegments()}
            </svg>

            {/* Legend */}
            <div className="flex justify-center items-center gap-4 mt-3 flex-wrap">
                {levels.map(level => (
                    <div key={level} className="flex items-center text-sm">
                        <div className="w-3 h-3 rounded-full bg-gray-300 mr-2"></div>
                        <span>{level}</span>
                    </div>
                ))}

                {Object.entries(sourceColors).map(([source, color]) => (
                    <div key={source} className="flex items-center text-sm">
                        <div className="w-3 h-3 rounded-full mr-2" style={{backgroundColor: color}}></div>
                        <span>{source}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Middle Stage Teacher Feedback Viewer Component
 * Displays the teacher's comprehensive feedback for middle stage submissions
 */
const MiddleStageTeacherFeedbackViewer = ({submission}) => {
    // If no submission or no teacher feedback, show a placeholder
    if (!submission || !submission.teacher_feedback) {
        return (
            <div className="text-center py-6">
                <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-3"/>
                <p className="mt-1 text-sm text-gray-500">
                    No teacher feedback available yet
                </p>
            </div>
        );
    }

    const tf = submission.teacher_feedback;

    return (
        <div className="space-y-6">
            {/* Progress Wheel Section */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-6">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                            <MessageSquare className="w-5 h-5 text-white"/>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 font-[poppins]">Student Progress Assessment</h3>
                    </div>
                </div>
                <div className="p-6">
                    <ProgressWheelViewer progressWheel={tf.progress_wheel}/>
                </div>
            </div>

            {/* Teacher's Assessment Section */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                            <MessageSquare className="w-5 h-5 text-white"/>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 font-[poppins]">Teacher's Assessment</h3>
                    </div>
                </div>
                <div className="p-6">
                    {/* Areas of Strength */}
                    {(tf.areas_of_strength && tf.areas_of_strength.length > 0) || tf.other_strength ? (
                        <div className="mb-8">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                Areas of Strength
                            </h4>
                            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                                <ul className="list-disc list-inside text-sm text-green-800 space-y-2">
                                    {tf.areas_of_strength && tf.areas_of_strength.map((strength, index) => (
                                        <li key={index} className="font-medium">{strength}</li>
                                    ))}
                                    {tf.other_strength && (
                                        <li className="font-medium">{tf.other_strength}</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    ) : null}

                    {/* Barriers to Success */}
                    {(tf.barriers_to_success && tf.barriers_to_success.length > 0) || tf.other_barrier ? (
                        <div className="mb-6">
                            <h4 className="font-semibold text-gray-900 mb-2">Barriers to Success</h4>
                            <div className="bg-yellow-50 rounded-md p-4">
                                <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                                    {tf.barriers_to_success && tf.barriers_to_success.map((barrier, index) => (
                                        <li key={index}>{barrier}</li>
                                    ))}
                                    {tf.other_barrier && (
                                        <li>{tf.other_barrier}</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    ) : null}

                    {/* Support Actions / Progress Plan */}
                    {tf.support_actions && tf.support_actions.length > 0 ? (
                        <div className="mb-6">
                            <h4 className="font-semibold text-gray-900 mb-2">Teacher's Plan to Support Progress</h4>
                            <div className="bg-blue-50 rounded-md p-4">
                                <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                                    {tf.support_actions.map((action, index) => (
                                        <li key={index}>{action}</li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    ) : null}

                    {/* Observations and Recommendations */}
                    {tf.observations ? (
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Teacher's Observations and
                                Recommendations</h4>
                            <div className="bg-purple-50 rounded-md p-4">
                                <p className="text-sm text-purple-800">
                                    {tf.observations}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default MiddleStageTeacherFeedbackViewer;